import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Brain, CheckSquare, Coins, Inbox, RefreshCw, Target, TrendingUp, Users, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { get } from "@/lib/api";
import { contentPath, runPath } from "@/lib/moduleLinks";
import { formatDate, formatDateTime, formatNumber, formatUsd } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { ErrorState } from "@/components/common/DataStates";
import { AgentChip } from "@/components/common/AgentChip";
import { StatusPill } from "@/components/common/StatusPill";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const QUICK_LINKS = [
  { key: "crm", path: "/app/crm" },
  { key: "approvals", path: "/app/approvals" },
  { key: "social", path: "/app/social" },
  { key: "brandBrain", path: "/app/brand-brain" },
  { key: "aiOps", path: "/app/ai" },
];

const KpiCard = ({ label, value, hint, icon: Icon, testId }) => (
  <div className="og-card flex min-w-0 items-start justify-between gap-2 p-4" data-testid={testId}>
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 break-words font-heading text-2xl font-semibold tracking-tight" data-testid={`${testId}-value`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-primary"><Icon className="h-4 w-4" aria-hidden="true" /></span>
  </div>
);

const Section = ({ title, to, children, testId, actionLabel }) => (
  <section className="og-card min-w-0 overflow-hidden" data-testid={testId}>
    <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
      <h2 className="font-heading text-sm font-semibold">{title}</h2>
      {to && <Link to={to} className="og-focus inline-flex shrink-0 items-center gap-1 rounded text-xs font-medium text-primary hover:underline">{actionLabel}<ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></Link>}
    </div>
    <div className="px-4 py-2">{children}</div>
  </section>
);

const Item = ({ to, title, detail, date, children }) => (
  <li className="border-b border-border/70 last:border-0">
    <Link to={to} className="og-focus flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded px-1 py-3 hover:bg-muted/40 sm:flex-nowrap">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{title}</div>
        {detail && <div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">{children}{date && <span>{date}</span>}</div>
    </Link>
  </li>
);

const Empty = ({ children }) => <p className="py-5 text-sm text-muted-foreground">{children}</p>;

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user, currentOrg, orgId } = useAuth();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["dashboard-overview", orgId],
    queryFn: () => get("/analytics/overview"),
    enabled: !!orgId,
  });
  const brainQuery = useQuery({ queryKey: ["brand-brain", orgId], queryFn: () => get("/brand-brain"), enabled: !!orgId });
  const firstName = user?.full_name?.split(" ")[0] || "";

  const kpis = data?.kpis;
  const validOverview = kpis
    && ["leads_total", "leads_week", "opportunities_open", "pipeline_value", "tasks_open", "tasks_overdue", "approvals_pending", "ai_cost_month_usd", "agent_runs_week"].every((key) => typeof kpis[key] === "number" && Number.isFinite(kpis[key]))
    && ["recent_leads", "pending_approvals", "tasks", "recent_runs", "ai_by_agent", "upcoming_content"].every((key) => Array.isArray(data[key]));
  const metrics = kpis && [
    { key: "leads-total", label: t("dashboard.operational.leadsTotal"), value: formatNumber(kpis.leads_total), icon: Users },
    { key: "leads-week", label: t("dashboard.operational.leadsWeek"), value: formatNumber(kpis.leads_week), icon: Target },
    { key: "opportunities", label: t("dashboard.operational.opportunities"), value: formatNumber(kpis.opportunities_open), icon: TrendingUp },
    { key: "pipeline", label: t("dashboard.operational.pipeline"), value: formatNumber(kpis.pipeline_value, { maximumFractionDigits: 2 }), hint: t("dashboard.operational.pipelineUnit"), icon: TrendingUp },
    { key: "tasks-open", label: t("dashboard.operational.tasksOpen"), value: formatNumber(kpis.tasks_open), icon: CheckSquare },
    { key: "tasks-overdue", label: t("dashboard.operational.tasksOverdue"), value: formatNumber(kpis.tasks_overdue), icon: CheckSquare },
    { key: "approvals", label: t("dashboard.operational.approvals"), value: formatNumber(kpis.approvals_pending), icon: Inbox },
    { key: "ai-cost", label: t("dashboard.operational.aiCost"), value: formatUsd(kpis.ai_cost_month_usd, kpis.ai_cost_month_usd >= 0.1 ? 2 : 4), icon: Coins },
    { key: "runs-week", label: t("dashboard.operational.runsWeek"), value: formatNumber(kpis.agent_runs_week), icon: Zap },
  ];
  const brain = brainQuery.data;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader title={t("dashboard.greeting", { name: firstName })} subtitle={t("dashboard.subtitle", { org: currentOrg?.name })} testId="dashboard-header" actions={
        <Button variant="outline" size="sm" className="bg-card" onClick={() => refetch()} disabled={!orgId || isFetching} data-testid="dashboard-refresh">
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />{t("dashboard.operational.refresh")}
        </Button>
      } />

      <nav aria-label={t("dashboard.operational.quickLinks")} data-testid="dashboard-quick-links">
        <h2 className="og-section-label mb-2">{t("dashboard.operational.quickLinks")}</h2>
        <div className="flex flex-wrap gap-2">
          {QUICK_LINKS.map(({ key, path }) => (
            <Button key={key} asChild variant="outline" size="sm" className="bg-card"><Link to={path}>{t(`nav.${key}`)}</Link></Button>
          ))}
        </div>
      </nav>

      {isError ? <ErrorState message={error?.message} onRetry={refetch} testId="dashboard-overview-error" /> : isLoading || !orgId ? (
        <div aria-busy="true" data-testid="dashboard-overview-loading" className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">{Array.from({ length: 9 }, (_, i) => <Skeleton key={i} className="h-28 w-full rounded-lg" />)}</div>
          <div className="grid gap-4 lg:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-48 w-full rounded-lg" />)}</div>
        </div>
      ) : !validOverview ? <ErrorState message={t("dashboard.operational.invalidResponse")} onRetry={refetch} testId="dashboard-overview-error" /> : (
        <>
          <section aria-label={t("dashboard.operational.overview")} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-kpis">
            {metrics.map(({ key, ...metric }) => <KpiCard key={key} {...metric} testId={`dashboard-kpi-${key}`} />)}
          </section>

          <div className="grid items-start gap-4 lg:grid-cols-2">
            <Section title={t("dashboard.sections.leads")} to="/app/crm?tab=leads" actionLabel={t("common.seeAll")} testId="dashboard-recent-leads">
              {data.recent_leads?.length ? <ul>{data.recent_leads.map((lead) => (
                <Item key={lead.id} title={lead.title} detail={lead.company_name || lead.contact_name} date={formatDate(lead.created_at)} to={`/app/crm?tab=leads&record=lead:${lead.id}`}>
                  <StatusPill status={lead.status} />
                </Item>
              ))}</ul> : <Empty>{t("dashboard.emptyLeads")}</Empty>}
            </Section>

            <Section title={t("dashboard.sections.approvals")} to="/app/approvals?status=pending_approval" actionLabel={t("common.seeAll")} testId="dashboard-pending-approvals">
              {data.pending_approvals?.length ? <ul>{data.pending_approvals.map((approval) => (
                <Item key={approval.id} title={approval.title} detail={approval.summary} date={formatDate(approval.due_at || approval.created_at)} to={`/app/approvals?status=pending_approval&item=${approval.id}`} />
              ))}</ul> : <Empty>{t("dashboard.emptyApprovals")}</Empty>}
            </Section>

            <Section title={t("dashboard.sections.tasks")} to="/app/crm?tab=tasks" actionLabel={t("common.seeAll")} testId="dashboard-open-tasks">
              {data.tasks?.length ? <ul>{data.tasks.map((task) => (
                <Item key={task.id} title={task.title} detail={t(`status.${task.status}`, { defaultValue: task.status })} date={task.due_at ? `${t("dashboard.operational.due")} ${formatDate(task.due_at)}` : null} to="/app/crm?tab=tasks" />
              ))}</ul> : <Empty>{t("dashboard.emptyTasks")}</Empty>}
            </Section>

            <Section title={t("dashboard.sections.agents")} to="/app/ai" actionLabel={t("common.seeAll")} testId="dashboard-recent-runs">
              {data.recent_runs?.length ? <ul>{data.recent_runs.map((run) => (
                <Item key={run.id} title={run.task_type} date={formatDateTime(run.created_at)} to={runPath(run.id) || "/app/ai?tab=runs"}>
                  <AgentChip agentId={run.agent_id} /><StatusPill status={run.status} />
                </Item>
              ))}</ul> : <Empty>{t("dashboard.emptyRuns")}</Empty>}
            </Section>

            <Section title={t("dashboard.sections.ai")} to="/app/ai" actionLabel={t("common.seeAll")} testId="dashboard-ai-by-agent">
              {data.ai_by_agent?.length ? <ul>{data.ai_by_agent.map((agent) => (
                <li key={agent.agent_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 py-3 last:border-0">
                  <AgentChip agentId={agent.agent_id} persona={agent.persona} />
                  <span className="text-xs text-muted-foreground">{t("dashboard.operational.aiCalls", { count: agent.calls })} · {formatUsd(agent.cost_usd, agent.cost_usd >= 0.1 ? 2 : 4)}</span>
                </li>
              ))}</ul> : <Empty>{t("dashboard.operational.emptyAi")}</Empty>}
            </Section>

            <Section title={t("dashboard.operational.upcomingContent")} to="/app/social" actionLabel={t("common.seeAll")} testId="dashboard-upcoming-content">
              {data.upcoming_content?.length ? <ul>{data.upcoming_content.map((content) => (
                <Item key={content.id} title={content.title} date={formatDateTime(content.scheduled_at)} to={contentPath(content.id) || "/app/social"}>
                  <StatusPill status={content.status} />
                </Item>
              ))}</ul> : <Empty>{t("dashboard.operational.emptyContent")}</Empty>}
            </Section>
          </div>
        </>
      )}

      <section className="og-card p-5" data-testid="dashboard-brand-brain-card">
        <div className="flex items-center gap-2"><Brain className="h-4 w-4 text-primary" aria-hidden="true" /><h2 className="font-heading text-sm font-semibold">{t("dashboard.placeholder.brandBrainTitle")}</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.placeholder.brandBrainHint")}</p>
        {brainQuery.isError ? <ErrorState className="mt-4" message={brainQuery.error?.message} onRetry={brainQuery.refetch} testId="dashboard-brand-brain-error" /> :
          brainQuery.isLoading || !orgId ? <Skeleton className="mt-4 h-12 w-full" data-testid="dashboard-brand-brain-loading" /> : brain?.readiness ? (
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-heading text-2xl font-semibold" data-testid="dashboard-brand-brain-percent">{t("common.percent", { value: brain.completeness_score })}</span>
                  <Badge variant="outline" data-testid="dashboard-brand-brain-status">{brain.readiness.ready_for_agents ? t("dashboard.placeholder.brandBrainReady") : t("dashboard.placeholder.brandBrainNotReady", { count: brain.readiness.missing_required?.length ?? 0 })}</Badge>
                </div>
                <Progress value={brain.completeness_score} className="mt-2 h-2" />
              </div>
              <Button asChild size="sm"><Link to="/app/brand-brain">{t("dashboard.placeholder.openBrandBrain")}<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            </div>
          ) : <ErrorState className="mt-4" message={t("dashboard.operational.invalidResponse")} onRetry={brainQuery.refetch} testId="dashboard-brand-brain-error" />}
      </section>
    </div>
  );
}